import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CVRegistrationData,
  EntityType,
  IdentityType,
  NonprofitSubType,
  OrganizationDetails,
  RegistrationState,
  SampleMessages,
  UseCaseInfo,
} from '../types/registration';

const initialOrganizationDetails: OrganizationDetails = {
  ein: '',
  companyName: '',
  displayName: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  postalCode: '',
  phone: '',
  email: '',
  website: '',
  vertical: null,
  entityType: null,
  privacyPolicyUrl: '',
  signupPageUrl: '',
  tacUrl: '',
  jobAreas: [],
  willSendOver2k: null,
};

const initialSampleMessages: SampleMessages = {
  sample1: '',
  sample2: '',
  optOut1: '',
  optOut2: '',
  combined1: '',
  combined2: '',
};

const initialUseCaseInfo: UseCaseInfo = {
  description: '',
  usecase: '',
  subUsecases: [],
  vertical: null,
};

const initialState: RegistrationState = {
  // Progress tracking
  currentStep: 1, // Start with step 1 (Identity Type)
  completedSteps: [],

  // Identity info
  identityId: null,
  identityName: '',
  identityType: null,
  nonprofitSubType: null,

  // Flow control
  flowType: null,
  hasCvToken: null,

  // CV Token Flow State
  cvTokenStatus: null,
  cvRegistrationData: null,
  cvPinVerificationId: null,
  cvRequestStatus: null,

  // Form data
  organizationDetails: initialOrganizationDetails,
  sampleMessages: initialSampleMessages,
  useCaseInfo: initialUseCaseInfo,
  campaignVerifyToken: '',

  // API state
  isSubmitting: false,
  submissionError: null,
  apiErrors: {},

  // Validation
  stepValidation: {},
};

export const identityRegistrationSlice = createSlice({
  name: 'identityRegistration',
  initialState,
  reducers: {
    // Progress actions
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    addCompletedStep: (state, action: PayloadAction<number>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },
    removeCompletedStep: (state, action: PayloadAction<number>) => {
      state.completedSteps = state.completedSteps.filter(
        (step) => step !== action.payload
      );
    },

    // Identity info actions
    setIdentityId: (state, action: PayloadAction<string>) => {
      state.identityId = action.payload;
    },
    setIdentityName: (state, action: PayloadAction<string>) => {
      state.identityName = action.payload;
    },
    setIdentityType: (state, action: PayloadAction<IdentityType>) => {
      state.identityType = action.payload;
      state.flowType = action.payload === 'political' ? 'political' : 'non-political';

      // Reset downstream choices that depend on identity type
      state.useCaseInfo = initialUseCaseInfo;
      state.hasCvToken = null;
      state.cvTokenStatus = null;
      state.cvRegistrationData = null;
      state.cvPinVerificationId = null;
      state.cvRequestStatus = null;
      state.campaignVerifyToken = '';

      // Set initial entity type and vertical based on identity type
      if (action.payload === 'political') {
        state.organizationDetails.entityType = 'NON_PROFIT';
        state.organizationDetails.vertical = 'POLITICAL';
        state.nonprofitSubType = null;
      } else if (action.payload === 'non-profit') {
        state.organizationDetails.entityType = 'NON_PROFIT';
        state.organizationDetails.vertical = 'NGO';
        state.nonprofitSubType = null;
      } else if (action.payload === 'government') {
        state.organizationDetails.entityType = 'GOVERNMENT';
        state.organizationDetails.vertical = 'GOVERNMENT';
        state.nonprofitSubType = null;
      } else if (action.payload === 'private-company') {
        state.organizationDetails.entityType = 'PRIVATE_PROFIT';
        state.organizationDetails.vertical = null;
        state.nonprofitSubType = null;
      }
    },
    setNonprofitSubType: (state, action: PayloadAction<NonprofitSubType | null>) => {
      state.nonprofitSubType = action.payload;
      state.useCaseInfo = initialUseCaseInfo;
    },

    // Flow control actions
    setHasCvToken: (state, action: PayloadAction<boolean | null>) => {
      state.hasCvToken = action.payload;
    },

    // CV Token Status actions
    setCvTokenStatus: (
      state,
      action: PayloadAction<'existing' | 'register' | 'verify_pin' | null>
    ) => {
      state.cvTokenStatus = action.payload;
    },

    // CV Registration Data actions
    setCvRegistrationData: (
      state,
      action: PayloadAction<Partial<CVRegistrationData>>
    ) => {
      if (state.cvRegistrationData) {
        state.cvRegistrationData = { ...state.cvRegistrationData, ...action.payload };
      } else {
        // Initialize with the payload if cvRegistrationData is null
        state.cvRegistrationData = action.payload as CVRegistrationData;
      }
    },
    clearCvRegistrationData: (state) => {
      state.cvRegistrationData = null;
    },

    // CV PIN Verification actions
    setCvPinVerificationId: (state, action: PayloadAction<string | null>) => {
      state.cvPinVerificationId = action.payload;
    },
    setCvRequestStatus: (
      state,
      action: PayloadAction<'REQUESTED' | 'APPROVED' | 'DENIED' | null>
    ) => {
      state.cvRequestStatus = action.payload;
    },

    // Organization details actions
    setOrganizationDetails: (
      state,
      action: PayloadAction<Partial<OrganizationDetails>>
    ) => {
      state.organizationDetails = { ...state.organizationDetails, ...action.payload };
    },
    setEin: (state, action: PayloadAction<string>) => {
      state.organizationDetails.ein = action.payload;
    },
    setCompanyName: (state, action: PayloadAction<string>) => {
      state.organizationDetails.companyName = action.payload;
    },
    setDisplayName: (state, action: PayloadAction<string>) => {
      state.organizationDetails.displayName = action.payload;
    },
    setStreet: (state, action: PayloadAction<string>) => {
      state.organizationDetails.street = action.payload;
    },
    setCity: (state, action: PayloadAction<string>) => {
      state.organizationDetails.city = action.payload;
    },
    setState: (state, action: PayloadAction<string>) => {
      state.organizationDetails.state = action.payload;
    },
    setPostalCode: (state, action: PayloadAction<string>) => {
      state.organizationDetails.postalCode = action.payload;
    },
    setPhone: (state, action: PayloadAction<string>) => {
      state.organizationDetails.phone = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.organizationDetails.email = action.payload;
    },
    setWebsite: (state, action: PayloadAction<string>) => {
      state.organizationDetails.website = action.payload;
    },
    setVertical: (state, action: PayloadAction<string | null>) => {
      state.organizationDetails.vertical = action.payload;
    },
    setEntityType: (state, action: PayloadAction<EntityType>) => {
      state.organizationDetails.entityType = action.payload;
    },
    setPrivacyPolicyUrl: (state, action: PayloadAction<string>) => {
      state.organizationDetails.privacyPolicyUrl = action.payload;
    },
    setSignupPageUrl: (state, action: PayloadAction<string>) => {
      state.organizationDetails.signupPageUrl = action.payload;
    },
    setWillSendOver2k: (state, action: PayloadAction<boolean>) => {
      state.organizationDetails.willSendOver2k = action.payload;
    },

    // Sample messages actions
    setSampleMessage1: (state, action: PayloadAction<string>) => {
      state.sampleMessages.sample1 = action.payload;
    },
    setSampleMessage2: (state, action: PayloadAction<string>) => {
      state.sampleMessages.sample2 = action.payload;
    },
    setSampleMessages: (state, action: PayloadAction<SampleMessages>) => {
      state.sampleMessages = action.payload;
    },
    setOptOutMessage1: (state, action: PayloadAction<string>) => {
      state.sampleMessages.optOut1 = action.payload;
    },
    setOptOutMessage2: (state, action: PayloadAction<string>) => {
      state.sampleMessages.optOut2 = action.payload;
    },
    setCombinedMessage1: (state, action: PayloadAction<string>) => {
      state.sampleMessages.combined1 = action.payload;
    },
    setCombinedMessage2: (state, action: PayloadAction<string>) => {
      state.sampleMessages.combined2 = action.payload;
    },

    // Use case actions
    setUseCaseDescription: (state, action: PayloadAction<string>) => {
      state.useCaseInfo.description = action.payload;
    },
    setUseCase: (state, action: PayloadAction<string>) => {
      state.useCaseInfo.usecase = action.payload;
    },
    setSubUseCases: (state, action: PayloadAction<string[]>) => {
      state.useCaseInfo.subUsecases = action.payload;
    },
    setUseCaseVertical: (state, action: PayloadAction<string | null>) => {
      state.useCaseInfo.vertical = action.payload;
    },

    // Campaign Verify Token actions
    setCampaignVerifyToken: (state, action: PayloadAction<string>) => {
      state.campaignVerifyToken = action.payload;
    },

    // CV Token pre-fill action
    prefillOrganizationDetails: (
      state,
      action: PayloadAction<Partial<OrganizationDetails>>
    ) => {
      // Merge the pre-filled data with existing organization details
      state.organizationDetails = {
        ...state.organizationDetails,
        ...action.payload,
      };
    },

    // API state actions
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setSubmissionError: (state, action: PayloadAction<string | null>) => {
      state.submissionError = action.payload;
    },
    setApiError: (state, action: PayloadAction<{ key: string; error: string }>) => {
      state.apiErrors[action.payload.key] = action.payload.error;
    },
    clearApiError: (state, action: PayloadAction<string>) => {
      delete state.apiErrors[action.payload];
    },
    clearAllApiErrors: (state) => {
      state.apiErrors = {};
    },

    // Validation actions
    setStepValidation: (
      state,
      action: PayloadAction<{ step: number; errors: string[] }>
    ) => {
      state.stepValidation[action.payload.step] = action.payload.errors;
    },
    clearStepValidation: (state, action: PayloadAction<number>) => {
      delete state.stepValidation[action.payload];
    },
    clearAllValidation: (state) => {
      state.stepValidation = {};
    },

    // Reset action
    resetRegistration: () => {
      return initialState;
    },
  },
});

export const {
  // Progress actions
  setCurrentStep,
  addCompletedStep,
  removeCompletedStep,

  // Identity info actions
  setIdentityId,
  setIdentityName,
  setIdentityType,
  setNonprofitSubType,

  // Flow control actions
  setHasCvToken,

  // CV Token Status actions
  setCvTokenStatus,

  // CV Registration Data actions
  setCvRegistrationData,
  clearCvRegistrationData,

  // CV PIN Verification actions
  setCvPinVerificationId,
  setCvRequestStatus,

  // Organization details actions
  setOrganizationDetails,
  setEin,
  setCompanyName,
  setDisplayName,
  setStreet,
  setCity,
  setState,
  setPostalCode,
  setPhone,
  setEmail,
  setWebsite,
  setVertical,
  setEntityType,
  setPrivacyPolicyUrl,
  setSignupPageUrl,
  setWillSendOver2k,

  // Sample messages actions
  setSampleMessage1,
  setSampleMessage2,
  setSampleMessages,
  setOptOutMessage1,
  setOptOutMessage2,
  setCombinedMessage1,
  setCombinedMessage2,

  // Use case actions
  setUseCaseDescription,
  setUseCase,
  setSubUseCases,
  setUseCaseVertical,

  // Campaign verify token action
  setCampaignVerifyToken,

  // CV Token pre-fill action
  prefillOrganizationDetails,

  // API state actions
  setIsSubmitting,
  setSubmissionError,
  setApiError,
  clearApiError,
  clearAllApiErrors,

  // Validation actions
  setStepValidation,
  clearStepValidation,
  clearAllValidation,

  // Reset action
  resetRegistration,
} = identityRegistrationSlice.actions;

export default identityRegistrationSlice;
